import * as babylon from "@babylonjs/core";

import type { BabylonState, Vector3Plain } from "../../state";

import {
  angularToleranceFor,
  nearestPointToRay,
  nearestVectorToRay,
} from "./nearestPointToRay";

/**
 * Cursor tolerance for point-cloud picking, in framebuffer pixels (the units
 * of `engine.getRenderHeight()`). A ray never strikes a point exactly, so a
 * pick accepts the nearest point within this radius of the cursor. Several
 * times the rendered size of a cloud point, so aiming at a visible dot always
 * lands it.
 */
export const PICK_RADIUS_PX = 8;

export interface ScenePick {
  /**
   * The picked location in data space: the render-space hit plus
   * `camera.focusPoint`, undoing `applyRenderOrigin`'s shift. Stable across
   * frames, unlike render-space coordinates, whose origin follows the camera.
   */
  dataPoint: Vector3Plain;
  /**
   * Distance to the hit along the pick ray, in scene units, measured from the
   * ray's origin on the camera near plane rather than from the camera itself.
   */
  distance: number;
  /**
   * Log time of the trajectory sample the pick landed on. Present only for a
   * path-sample hit; a surface or cloud point answers for no single moment.
   */
  sampleTime?: bigint;
}

/**
 * A hit as the mesh pick strategies find it: in render space, before the
 * render-origin shift is undone.
 */
interface RenderPick {
  renderPoint: babylon.Vector3;
  distance: number;
}

/**
 * A hit in data space, the frame the strategies are arbitrated in once the
 * render-origin shift is undone.
 */
interface DataPick {
  dataPoint: babylon.Vector3;
  distance: number;
  sampleTime?: bigint;
}

/** An entity's drawn trajectory: the samples behind its path trace. */
interface PathSamples {
  points: babylon.Vector3[];
  logtimes: bigint[];
}

/**
 * What the cursor at canvas position `(x, y)` is over, or `undefined` for the
 * background. A pick always means a point *on the data*, never a point in
 * empty space.
 *
 * `(x, y)` is canvas-relative CSS px, as `attachToolPointerInput` records it.
 * `scene.createPickingRay` takes that space and divides out the engine's
 * hardware scaling itself.
 *
 * Only what an entity draws is pickable, by three strategies. Triangle
 * geometry — primitive meshes and model geometry — is picked by Babylon's
 * raycast. A points-mode mesh (`material.pointsCloud`) has no triangles for a
 * ray to strike, so it is picked by nearest-point-to-ray within
 * {@link PICK_RADIUS_PX} of the cursor. A path trace is picked the same way,
 * but against the trajectory samples it was drawn from rather than its ribbon
 * mesh: a trace's samples are the data, and a point between two of them is
 * interpolation. The grid and vector fields stay unpickable.
 *
 * The nearest hit to the camera wins, in world-metric distance across all
 * three.
 *
 * Reads the world matrices the last rendered frame wrote, so call it between
 * frames (input handlers, post-render systems), not before `applyRenderOrigin`
 * has placed meshes for the current frame.
 */
export function pickScenePoint(
  state: BabylonState,
  x: number,
  y: number,
): ScenePick | undefined {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return undefined;
  }
  const scene = state.context.scene;

  const { triangleMeshes, pointMeshes } = collectPickableMeshes(state);
  const paths = collectPathSamples(state);

  let best: DataPick | undefined;

  const rayPick = scene.pick(
    x,
    y,
    (mesh) => triangleMeshes.has(mesh),
    false,
    camera.babylonCamera,
  );
  if (rayPick.hit && rayPick.pickedPoint) {
    best = {
      dataPoint: rayPick.pickedPoint.add(camera.focusPoint),
      distance: rayPick.distance,
    };
  }

  if (pointMeshes.size > 0 || paths.length > 0) {
    const ray = scene.createPickingRay(x, y, null, camera.babylonCamera);
    const tanHalfAngle = angularToleranceFor(
      PICK_RADIUS_PX,
      state.context.engine.getRenderHeight(),
      camera.babylonCamera.fov,
    );

    for (const mesh of pointMeshes) {
      const hit = pickCloudMesh(mesh, ray, tanHalfAngle);
      if (hit && (best === undefined || hit.distance < best.distance)) {
        best = {
          dataPoint: hit.renderPoint.addInPlace(camera.focusPoint),
          distance: hit.distance,
        };
      }
    }

    // Samples are absolute float64 coordinates, so the search runs in data
    // space: the ray origin moves there once rather than every sample being
    // shifted into render space. The shift is a translation, so the direction
    // and the depths it produces carry over unchanged and stay comparable
    // with the mesh hits.
    if (paths.length > 0) {
      const dataRay = {
        origin: ray.origin.add(camera.focusPoint),
        direction: ray.direction,
      };
      for (const path of paths) {
        const hit = nearestVectorToRay(path.points, dataRay, tanHalfAngle);
        if (hit && (best === undefined || hit.t < best.distance)) {
          best = {
            dataPoint: path.points[hit.index],
            distance: hit.t,
            sampleTime: path.logtimes[hit.index],
          };
        }
      }
    }
  }

  if (best === undefined) {
    return undefined;
  }
  return {
    dataPoint: {
      x: best.dataPoint.x,
      y: best.dataPoint.y,
      z: best.dataPoint.z,
    },
    distance: best.distance,
    sampleTime: best.sampleTime,
  };
}

/**
 * The trajectory samples behind every path trace currently drawn, from the
 * cache the trace is baked from — `reloadStalePaths` reads exactly this array,
 * so a pick lands on a sample the user can see.
 *
 * A trace is drawn only for a positive line width and a loaded, non-empty
 * cache; anything else contributes no geometry and so is not pickable. Ties
 * picking to the same condition that bumps `entityGeneration`, which is what
 * lets `reportToolHover` skip a pick after a reload that changed nothing.
 */
function collectPathSamples(state: BabylonState): PathSamples[] {
  const paths: PathSamples[] = [];
  for (const entity of Object.values(state.entities)) {
    const lineWidth = entity.path?.spec.lineWidth;
    const cache = entity.globalPosition?.cache;
    if (
      lineWidth === undefined ||
      !isFinite(lineWidth) ||
      lineWidth <= 0 ||
      cache === undefined ||
      cache.load.state !== "loaded" ||
      cache.allPoints.length === 0
    ) {
      continue;
    }
    paths.push({ points: cache.allPoints, logtimes: cache.logtimes });
  }
  return paths;
}

/**
 * Fill modes `AbstractMesh.intersects` cannot raycast. For these it reports a
 * bounding-box pseudo-hit (`hit: true`, `pickedPoint: null`) that competes on
 * distance inside `scene.pick` and can shadow real, pickable geometry behind
 * it, so they are kept out of the raycast bucket entirely. glTF's
 * `LINE_LOOP`, `LINE_STRIP` and `TRIANGLE_FAN` primitives load with these
 * modes.
 */
const NON_INTERSECTABLE_FILL_MODES: ReadonlySet<number> = new Set([
  babylon.Material.LineLoopDrawMode,
  babylon.Material.LineStripDrawMode,
  babylon.Material.TriangleFanDrawMode,
]);

/**
 * Every mesh the entities draw, split by pick strategy: point-cloud materials
 * (`pointsCloud`) go to nearest-point-to-ray, everything else to the raycast.
 * A model contributes every mesh below its anchor. An `InstancedMesh` reports
 * its source's material, so an instanced cloud node sorts correctly. Fill
 * modes no ray can strike ({@link NON_INTERSECTABLE_FILL_MODES}) are left
 * out of both.
 */
function collectPickableMeshes(state: BabylonState): {
  triangleMeshes: Set<babylon.AbstractMesh>;
  pointMeshes: Set<babylon.AbstractMesh>;
} {
  const triangleMeshes = new Set<babylon.AbstractMesh>();
  const pointMeshes = new Set<babylon.AbstractMesh>();
  const add = (mesh: babylon.AbstractMesh) => {
    const material = mesh.material;
    if (material?.pointsCloud) {
      pointMeshes.add(mesh);
    } else if (
      !material ||
      !NON_INTERSECTABLE_FILL_MODES.has(material.fillMode)
    ) {
      triangleMeshes.add(mesh);
    }
  };

  for (const entity of Object.values(state.entities)) {
    if (entity.mesh?.mesh) {
      add(entity.mesh.mesh);
    }
    // Walk the anchor rather than `model.nodesByName`: that map keeps one
    // node per name, glTF allows duplicate names, and indexing it would leave
    // a visible duplicate unpickable.
    const anchor = entity.mesh?.model?.anchor;
    if (anchor) {
      for (const node of anchor.getChildMeshes(false)) {
        add(node);
      }
    }
  }
  return { triangleMeshes, pointMeshes };
}

/**
 * Vertex positions read off a mesh once and reused for every pick. Sound
 * because the panel's only points-mode geometry is a loaded asset's, whose
 * vertices never change; a mesh that rewrote them would need this cache
 * invalidated.
 *
 * Keyed by the `Geometry` rather than the mesh, because instances and clones
 * of one cloud share a geometry. The cache matters because `getVerticesData`
 * builds a fresh full-size `Float32Array` for an interleaved, quantized or
 * non-float buffer — the shape large clouds ship in. Meshes without a
 * geometry fall back to keying on themselves.
 */
const cloudPositionsCache = new WeakMap<object, ArrayLike<number>>();

function cloudPositions(
  mesh: babylon.AbstractMesh,
): ArrayLike<number> | undefined {
  const key =
    mesh instanceof babylon.Mesh || mesh instanceof babylon.InstancedMesh
      ? (mesh.geometry ?? mesh)
      : mesh;
  const cached = cloudPositionsCache.get(key);
  if (cached) {
    return cached;
  }
  const positions = mesh.getVerticesData(babylon.VertexBuffer.PositionKind);
  if (positions) {
    cloudPositionsCache.set(key, positions);
  }
  return positions ?? undefined;
}

/**
 * Nearest cloud point of one mesh to the pick ray, as a render-space point
 * and its camera distance.
 *
 * The search runs in the mesh's local frame: the ray is transformed in,
 * rather than every point out. The transformed direction's length is the
 * world-to-local scale, so dividing the local depth by it recovers the world
 * distance. The angular tolerance is carried over unchanged, which is exact
 * for rigid-plus-uniform-scale transforms; a non-uniformly scaled cloud
 * would distort the cone.
 */
function pickCloudMesh(
  mesh: babylon.AbstractMesh,
  ray: babylon.Ray,
  tanHalfAngle: number,
): RenderPick | undefined {
  const positions = cloudPositions(mesh);
  if (!positions || positions.length < 3) {
    return undefined;
  }
  // The world matrix the last rendered frame computed — the frame the cursor
  // position answers for, and the same matrix Babylon's picker uses on the
  // triangle side.
  const worldMatrix = mesh.getWorldMatrix();
  const inverse = babylon.Matrix.Invert(worldMatrix);
  const localOrigin = babylon.Vector3.TransformCoordinates(ray.origin, inverse);
  const localDirection = babylon.Vector3.TransformNormal(
    ray.direction,
    inverse,
  );
  const scale = localDirection.length();
  if (scale === 0) {
    return undefined;
  }
  localDirection.scaleInPlace(1 / scale);

  const hit = nearestPointToRay(
    positions,
    { origin: localOrigin, direction: localDirection },
    tanHalfAngle,
  );
  if (!hit) {
    return undefined;
  }
  const localPoint = new babylon.Vector3(
    positions[hit.index * 3],
    positions[hit.index * 3 + 1],
    positions[hit.index * 3 + 2],
  );
  return {
    renderPoint: babylon.Vector3.TransformCoordinates(localPoint, worldMatrix),
    distance: hit.t / scale,
  };
}
